import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const KEY = 'ff_newsletter_popup_dismissed_at';
const WEEK = 7 * 24 * 60 * 60 * 1000;
const LOGO = 'https://customer-assets.emergentagent.com/job_style-central-65/artifacts/n560cpi8_Untitled%20design%20%2820%29.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT = 'w-full rounded-full bg-white/5 border border-orange-500/40 text-white placeholder:text-gray-500 px-5 py-3 outline-none focus:border-orange-500 focus:shadow-[0_0_18px_rgba(255,106,0,0.45)] transition-[border-color,box-shadow]';

export const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const last = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - last < WEEK) return;
    const t = setTimeout(() => setOpen(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(KEY, String(Date.now()));
    setOpen(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const { data } = await axios.post(`${API}/newsletter/signup`, form);
      setCode(data.code);
      localStorage.setItem(KEY, String(Date.now() + 3650 * 24 * 60 * 60 * 1000));
      setStatus('done');
    } catch (err) {
      setStatus(err.response?.status === 409 ? 'exists' : 'error');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    setCopied(true);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="newsletter-popup"
        className="max-w-md w-[calc(100%-2rem)] border border-orange-500/40 bg-black/80 backdrop-blur-xl text-white rounded-3xl sm:rounded-3xl p-8 text-center shadow-[0_0_60px_rgba(255,106,0,0.35)]">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full bg-orange-500/30 blur-[90px] pointer-events-none" />
        <img src={LOGO} alt="Finest Feathers" className="relative h-14 mx-auto mb-4 drop-shadow-[0_0_12px_rgba(255,106,0,0.8)]" />
        {status === 'done' ? (
          <div className="relative" data-testid="newsletter-popup-success">
            <CheckCircle2 className="w-14 h-14 text-orange-500 mx-auto mb-4 drop-shadow-[0_0_14px_#ff6a00]" />
            <DialogTitle className="text-4xl font-bold heading-oswald uppercase mb-3">You're in!</DialogTitle>
            <DialogDescription className="text-gray-300 mb-5">Welcome to the tribe. Use this code at checkout for 20% off your order:</DialogDescription>
            <div className="rounded-2xl border-2 border-dashed border-orange-500 bg-orange-500/10 py-4 mb-3 shadow-[0_0_25px_rgba(255,106,0,0.35)]">
              <p className="text-4xl font-bold heading-oswald tracking-[0.2em] text-orange-400 [text-shadow:0_0_16px_#ff6a00]" data-testid="newsletter-promo-code">{code}</p>
            </div>
            <button onClick={copy} data-testid="newsletter-copy-code-btn" className="w-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600 text-black font-bold py-3 uppercase tracking-wider mb-3 hover:-translate-y-0.5 transition-transform">
              {copied ? 'Copied!' : 'Copy code'}
            </button>
            <p className="text-xs text-gray-500 mb-5">One use per customer. Save this code, it won't be shown again.</p>
            <div className="flex flex-col gap-2">
              <a href="https://shop.finestfeathers.com" target="_blank" rel="noopener noreferrer" data-testid="newsletter-shop-now-btn" className="rounded-full border border-orange-500/60 px-8 py-3 font-semibold hover:bg-orange-500/10 transition-colors">Shop now</a>
              <button onClick={close} data-testid="newsletter-popup-close-btn" className="text-sm text-gray-400 hover:text-white transition-colors">Keep exploring</button>
            </div>
          </div>
        ) : (
          <>
            <p className="relative text-orange-400 text-xs font-semibold tracking-[0.3em] uppercase mb-2">Join the tribe</p>
            <DialogTitle className="relative text-5xl font-bold heading-oswald uppercase leading-none mb-3">
              Get <span className="text-orange-500 [text-shadow:0_0_18px_#ff6a00]">20% off</span>
            </DialogTitle>
            <DialogDescription className="relative text-gray-300 mb-6">
              Subscribe to our newsletter for 20% off your next order, plus first access to new drops, events and music.
            </DialogDescription>
            <form onSubmit={submit} className="relative space-y-3" data-testid="newsletter-popup-form">
              <div className="grid grid-cols-2 gap-3">
                <input required value={form.first_name} onChange={set('first_name')} placeholder="First name" className={INPUT} data-testid="newsletter-first-name-input" />
                <input required value={form.last_name} onChange={set('last_name')} placeholder="Last name" className={INPUT} data-testid="newsletter-last-name-input" />
              </div>
              <input required type="email" value={form.email} onChange={set('email')} placeholder="Email address" className={INPUT} data-testid="newsletter-email-input" />
              {status === 'error' && <p className="text-red-400 text-sm" data-testid="newsletter-popup-error">Something went wrong. Please try again.</p>}
              {status === 'exists' && <p className="text-orange-300 text-sm" data-testid="newsletter-popup-exists">You're already subscribed with this email.</p>}
              <button type="submit" disabled={status === 'sending'} data-testid="newsletter-popup-signup-btn"
                className="w-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600 text-black font-bold py-4 text-lg uppercase tracking-wider shadow-[0_0_25px_rgba(255,106,0,0.6)] hover:shadow-[0_0_40px_rgba(255,106,0,0.9)] hover:-translate-y-0.5 transition-[transform,box-shadow] disabled:opacity-60">
                {status === 'sending' ? 'Subscribing...' : 'Subscribe & Save 20%'}
              </button>
            </form>
            <button onClick={close} data-testid="newsletter-popup-dismiss-btn" className="relative mt-4 text-sm text-gray-400 hover:text-white transition-colors">
              No thanks
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
